import { Component, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { BancoPlazaService } from '../../services/banco-plaza.service';
import { UserService } from '../../services/user/user.service';

@Component({
  selector: 'app-banco-plaza-console',
  templateUrl: './banco-plaza-console.component.html',
  styleUrls: ['./banco-plaza-console.component.css']
})
export class BancoPlazaConsoleComponent implements OnInit {

  // Exponer Object y Math para el template
  Object = Object;
  Math = Math;

  // Pestaña activa
  activeTab: 'historial' | 'stuck' | 'configuracion' = 'historial';

  // Subpestaña dentro de "Historial"
  activeHistorialSubTab: 'client' | 'payer' | 'transaction' = 'client';

  // Subpestaña dentro de "En cola"
  activeStuckSubTab: 'queued' | 'unverified' | 'stuck' | 'anomalies' = 'queued';

  // Datos del cliente
  clientIdentity: string = '';
  clientData: any = null;
  codePerson: string = '';
  loadingClient: boolean = false;

  // Configuración
  config: any = null;
  loadingConfig: boolean = false;
  savingConfig: boolean = false;
  configForm = {
    max_token_attempts: 3,
    max_payment_retries: 3,
    max_verification_attempts: 10,
    verification_delay_seconds: 3,
    max_cron_verification_attempts: 45
  };

  loadQueuedTransactions = async () => {
    this.loadingQueuedTransactions = true;

    try {
      const response = await this.bancoPlazaService.getQueuedTransactions(
        this.queuedCurrentPage,
        this.queuedLimit,
        this.queuedSortOrder
      );

      if (response.success) {
        this.queuedTransactions = response.data.transactions;
        this.queuedTotalTransactions = response.data.pagination.total;
        this.queuedTotalPages = response.data.pagination.totalPages;
      }
    } catch (error: any) {
      console.error('Error cargando transacciones encoladas:', error);
      alert('Error cargando transacciones encoladas: ' + (error.error?.message || error.message));
    } finally {
      this.loadingQueuedTransactions = false;
    }
  };

  loadStuckTransactions = async () => {
    this.loadingStuckTransactions = true;

    try {
      const response = await this.bancoPlazaService.getStuckTransactions(
        this.stuckCurrentPage,
        this.stuckLimit,
        undefined,
        this.stuckSortOrder
      );

      if (response.success) {
        this.stuckTransactions = response.data.transactions;
        this.stuckTotalTransactions = response.data.pagination.total;
        this.stuckTotalPages = response.data.pagination.totalPages;
      }
    } catch (error: any) {
      console.error('Error cargando transacciones stuck:', error);
      alert('Error cargando transacciones stuck: ' + (error.error?.message || error.message));
    } finally {
      this.loadingStuckTransactions = false;
    }
  };

  changeStuckPage = (page: number) => {
    if (page >= 1 && page <= this.stuckTotalPages) {
      this.stuckCurrentPage = page;
      this.loadStuckTransactions();
    }
  };

  toggleStuckSortOrder = () => {
    this.stuckSortOrder = this.stuckSortOrder === -1 ? 1 : -1;
    this.stuckCurrentPage = 1;
    this.loadStuckTransactions();
  };

  changeQueuedPage = (page: number) => {
    if (page >= 1 && page <= this.queuedTotalPages) {
      this.queuedCurrentPage = page;
      this.loadQueuedTransactions();
    }
  };

  toggleQueuedSortOrder = () => {
    this.queuedSortOrder = this.queuedSortOrder === -1 ? 1 : -1;
    this.queuedCurrentPage = 1;
    this.loadQueuedTransactions();
  };

  loadAnomalies = async () => {
    this.loadingAnomalies = true;

    try {
      const response = await this.bancoPlazaService.getTransactionsWithAnomalies(
        this.anomaliesCurrentPage,
        this.anomaliesLimit,
        this.anomaliesSortOrder
      );

      if (response.success) {
        this.anomalies = response.data.anomalies;
        this.anomaliesTotalTransactions = response.data.pagination.total;
        this.anomaliesSummary = response.data.summary;
      }
    } catch (error: any) {
      console.error('Error cargando anomalías:', error);
      alert('Error cargando anomalías: ' + (error.error?.message || error.message));
    } finally {
      this.loadingAnomalies = false;
    }
  };

  changeAnomaliesPage = (page: number) => {
    const totalPages = Math.ceil(this.anomaliesTotalTransactions / this.anomaliesLimit);
    if (page >= 1 && page <= totalPages) {
      this.anomaliesCurrentPage = page;
      this.loadAnomalies();
    }
  };

  toggleAnomaliesSortOrder = () => {
    this.anomaliesSortOrder = this.anomaliesSortOrder === -1 ? 1 : -1;
    this.anomaliesCurrentPage = 1;
    this.loadAnomalies();
  };

  getSeverityClass = (severity: string): string => {
    const severityClasses: any = {
      'CRITICAL': 'bg-red-100 border-red-400 text-red-800',
      'HIGH': 'bg-orange-100 border-orange-400 text-orange-800',
      'MEDIUM': 'bg-yellow-100 border-yellow-400 text-yellow-800'
    };
    return severityClasses[severity] || 'bg-gray-100 border-gray-300 text-gray-700';
  };

  // Transacciones
  transactions: any[] = [];
  loadingTransactions: boolean = false;
  currentPage: number = 1;
  totalPages: number = 1;
  totalTransactions: number = 0;
  limit: number = 10;
  sortOrder: number = -1;

  stuckTransactions: any[] = [];
  loadingStuckTransactions: boolean = false;
  verifyingStuckTransactions: boolean = false;
  stuckCurrentPage: number = 1;
  stuckTotalPages: number = 1;
  stuckTotalTransactions: number = 0;
  stuckLimit: number = 10;
  stuckSortOrder: number = -1;

  stuckTransactionsList: any[] = [];
  loadingStuckTransactionsList: boolean = false;
  stuckTransactionsPage: number = 1;
  stuckTransactionsTotalPages: number = 1;
  stuckTransactionsCount: number = 0;
  stuckTransactionsLimit: number = 10;
  stuckTransactionsSortOrder: number = -1;

  queuedTransactions: any[] = [];
  loadingQueuedTransactions: boolean = false;
  queuedCurrentPage: number = 1;
  queuedTotalPages: number = 1;
  queuedTotalTransactions: number = 0;
  queuedLimit: number = 10;
  queuedSortOrder: number = -1;

  // Anomalías
  anomalies: any[] = [];
  loadingAnomalies: boolean = false;
  anomaliesCurrentPage: number = 1;
  anomaliesTotalTransactions: number = 0;
  anomaliesLimit: number = 50;
  anomaliesSortOrder: number = -1;
  anomaliesSummary: any = null;

  // Sincronización de estados MANUAL_COMPLETE
  syncingManualComplete: boolean = false;

  setActiveStuckSubTab = (tab: 'queued' | 'unverified' | 'stuck' | 'anomalies') => {
    this.activeStuckSubTab = tab;

    if (tab === 'queued') {
      this.queuedCurrentPage = 1;
      this.loadQueuedTransactions();
    } else if (tab === 'unverified') {
      this.stuckCurrentPage = 1;
      this.loadStuckTransactions();
    } else if (tab === 'stuck') {
      this.stuckTransactionsPage = 1;
      this.loadReallyStuckTransactions();
    } else if (tab === 'anomalies') {
      this.anomaliesCurrentPage = 1;
      this.loadAnomalies();
    }
  };

  verifyStuckTransactions = async () => {
    if (this.verifyingStuckTransactions) {
      return;
    }

    if (!confirm('¿Desea ejecutar verificación masiva para las transacciones en cola?')) {
      return;
    }

    this.verifyingStuckTransactions = true;

    try {
      const response = await this.bancoPlazaService.verifyPendingPayments();

      if (response?.success) {
        alert(`Verificación masiva completada. Verificadas: ${response.verified || 0}/${response.total || 0}. Errores: ${response.errors || 0}`);
      } else {
        alert(`Error en verificación masiva: ${response?.message || 'Error desconocido'}`);
      }
    } catch (error: any) {
      console.error('Error en verificación masiva:', error);
      alert('Error en verificación masiva: ' + (error.error?.message || error.message));
    } finally {
      this.verifyingStuckTransactions = false;

      // Refrescar listas según la subpestaña activa
      if (this.activeStuckSubTab === 'unverified') {
        await this.loadStuckTransactions();
      } else if (this.activeStuckSubTab === 'stuck') {
        await this.loadReallyStuckTransactions();
      }
      
      if (this.codePerson) {
        await this.loadTransactions();
      }
    }
  };

  loadReallyStuckTransactions = async () => {
    this.loadingStuckTransactionsList = true;

    try {
      const response = await this.bancoPlazaService.getReallyStuckTransactions(
        this.stuckTransactionsPage,
        this.stuckTransactionsLimit,
        undefined,
        this.stuckTransactionsSortOrder
      );

      if (response.success) {
        this.stuckTransactionsList = response.data.transactions;
        this.stuckTransactionsCount = response.data.pagination.total;
        this.stuckTransactionsTotalPages = response.data.pagination.totalPages;
      }
    } catch (error: any) {
      console.error('Error cargando transacciones atascadas:', error);
      alert('Error cargando transacciones atascadas: ' + (error.error?.message || error.message));
    } finally {
      this.loadingStuckTransactionsList = false;
    }
  };

  changeStuckTransactionsPage = (page: number) => {
    if (page >= 1 && page <= this.stuckTransactionsTotalPages) {
      this.stuckTransactionsPage = page;
      this.loadReallyStuckTransactions();
    }
  };

  toggleStuckTransactionsSortOrder = () => {
    this.stuckTransactionsSortOrder = this.stuckTransactionsSortOrder === -1 ? 1 : -1;
    this.stuckTransactionsPage = 1;
    this.loadReallyStuckTransactions();
  };

  // Filtros para búsqueda por cliente
  filters = {
    dateInitial: '',
    dateFinal: '',
    state: ''
  };

  // Búsqueda por transacción
  searchTransactionNumber: string = '';
  searchTransactions: any[] = [];
  loadingSearchTransactions: boolean = false;
  searchCurrentPage: number = 1;
  searchTotalPages: number = 1;
  searchTotalTransactions: number = 0;
  searchLimit: number = 10;
  searchSortOrder: number = -1;
  searchFilters = {
    dateInitial: '',
    dateFinal: '',
    state: ''
  };

  // Búsqueda por cédula del pagador
  payerIdentity: string = '';
  payerTransactions: any[] = [];
  loadingPayerTransactions: boolean = false;
  payerCurrentPage: number = 1;
  payerTotalPages: number = 1;
  payerTotalTransactions: number = 0;
  payerLimit: number = 10;
  payerSortOrder: number = -1;
  payerFilters = {
    dateInitial: '',
    dateFinal: '',
    state: ''
  };

  setActiveHistorialSubTab = (tab: 'client' | 'payer' | 'transaction') => {
    this.activeHistorialSubTab = tab;
  };

  searchTransactionsByNumber = async (isNewSearch: boolean = true) => {
    if (!this.searchTransactionNumber || this.searchTransactionNumber.trim() === '') {
      alert('Por favor ingrese un número de transacción');
      return;
    }

    this.loadingSearchTransactions = true;
    this.searchTransactions = [];
    
    if (isNewSearch) {
      this.searchCurrentPage = 1;
      // Resetear filtros al cambiar número de transacción
      this.searchFilters = {
        dateInitial: '',
        dateFinal: '',
        state: ''
      };
    }

    try {
      const response = await this.bancoPlazaService.searchTransactionsByNumber(
        this.searchTransactionNumber,
        this.searchFilters.dateInitial,
        this.searchFilters.dateFinal,
        this.searchFilters.state,
        this.searchCurrentPage,
        this.searchLimit,
        this.searchSortOrder
      );

      if (response.success) {
        this.searchTransactions = response.data.transactions;
        this.searchTotalTransactions = response.data.pagination.total;
        this.searchTotalPages = response.data.pagination.totalPages;
      }
    } catch (error: any) {
      console.error('Error buscando transacciones:', error);
      alert('Error buscando transacciones: ' + (error.error?.message || error.message));
    } finally {
      this.loadingSearchTransactions = false;
    }
  };

  changeSearchPage = (page: number) => {
    if (page >= 1 && page <= this.searchTotalPages) {
      this.searchCurrentPage = page;
      this.searchTransactionsByNumber(false);
    }
  };

  toggleSearchSortOrder = () => {
    this.searchSortOrder = this.searchSortOrder === -1 ? 1 : -1;
    this.searchCurrentPage = 1;
    this.searchTransactionsByNumber(false);
  };

  /**
   * Buscar transacciones por cédula del pagador
   */
  searchPayerTransactions = async (isNewSearch: boolean = true) => {
    if (!this.payerIdentity || this.payerIdentity.trim() === '') {
      alert('Por favor ingrese la cédula del pagador');
      return;
    }

    this.loadingPayerTransactions = true;
    this.payerTransactions = [];
    
    if (isNewSearch) {
      this.payerCurrentPage = 1;
      // Resetear filtros cuando se cambia la cédula
      this.payerFilters = {
        dateInitial: '',
        dateFinal: '',
        state: ''
      };
    }

    try {
      // Formatear fechas si existen
      let dateInitial = '';
      let dateFinal = '';

      if (this.payerFilters.dateInitial) {
        const [year, month, day] = this.payerFilters.dateInitial.split('-');
        dateInitial = `${month}/${day}/${year}`;
      }

      if (this.payerFilters.dateFinal) {
        const [year, month, day] = this.payerFilters.dateFinal.split('-');
        dateFinal = `${month}/${day}/${year}`;
      }

      const response = await this.bancoPlazaService.getTransactionsByPayerIdentity(
        this.payerIdentity.trim(),
        dateInitial,
        dateFinal,
        this.payerFilters.state,
        this.payerCurrentPage,
        this.payerLimit,
        this.payerSortOrder
      );

      if (response.success) {
        this.payerTransactions = response.data.transactions;
        this.payerTotalTransactions = response.data.pagination.total;
        this.payerTotalPages = response.data.pagination.totalPages;
        console.log('✅ Transacciones por pagador cargadas:', this.payerTransactions.length);
      }
    } catch (error: any) {
      console.error('Error buscando transacciones por cédula del pagador:', error);
      alert('Error buscando transacciones: ' + (error.error?.message || error.message));
    } finally {
      this.loadingPayerTransactions = false;
    }
  };

  /**
   * Aplicar filtros de búsqueda por pagador
   */
  applyPayerFilters = () => {
    this.payerCurrentPage = 1;
    this.searchPayerTransactions(false);
  };

  /**
   * Limpiar filtros de búsqueda por pagador
   */
  clearPayerFilters = () => {
    this.payerFilters = {
      dateInitial: '',
      dateFinal: '',
      state: ''
    };
    this.payerCurrentPage = 1;
    this.searchPayerTransactions();
  };

  /**
   * Limpiar búsqueda por pagador
   */
  clearPayerSearch = () => {
    this.payerIdentity = '';
    this.payerTransactions = [];
    this.payerFilters = {
      dateInitial: '',
      dateFinal: '',
      state: ''
    };
    this.payerCurrentPage = 1;
  };

  /**
   * Cambiar página de búsqueda por pagador
   */
  changePayerPage = (page: number) => {
    if (page >= 1 && page <= this.payerTotalPages) {
      this.payerCurrentPage = page;
      this.searchPayerTransactions(false);
    }
  };

  togglePayerSortOrder = () => {
    this.payerSortOrder = this.payerSortOrder === -1 ? 1 : -1;
    this.payerCurrentPage = 1;
    this.searchPayerTransactions(false);
  };

  // Bitácoras
  expandedTransaction: number | null = null;
  bitacoras: any[] = [];
  loadingBitacoras: boolean = false;

  // Modal de detalle de pago
  isPaymentDetailModalOpen = false;
  selectedPaymentDetail: any = null;
  isLoadingPaymentDetail = false;

  constructor(
    private bancoPlazaService: BancoPlazaService,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private userService: UserService
  ) { }

  ngOnInit(): void {
    // Cargar configuración al iniciar
    if (this.activeTab === 'configuracion') {
      this.loadConfig();
    }
  }

  /**
   * Cambiar pestaña activa
   */
  setActiveTab = (tab: 'historial' | 'stuck' | 'configuracion') => {
    this.activeTab = tab;
    
    if (tab === 'configuracion' && !this.config) {
      this.loadConfig();
    }

    if (tab === 'historial') {
      // Por defecto mostrar "Buscar Cliente"
      this.setActiveHistorialSubTab('client');
    }

    if (tab === 'stuck') {
      // Por defecto mostrar "Encoladas"
      this.setActiveStuckSubTab('queued');
    }
  };

  /**
   * Buscar cliente por cédula
   */
  searchClient = async () => {
    if (!this.clientIdentity || this.clientIdentity.trim() === '') {
      alert('Por favor ingrese una cédula');
      return;
    }

    this.loadingClient = true;
    this.clientData = null;
    this.transactions = [];
    this.codePerson = '';
    
    // Resetear paginación y filtros al cambiar de cliente
    this.currentPage = 1;
    this.filters = {
      dateInitial: '',
      dateFinal: '',
      state: ''
    };

    try {
      const searchData = {
        type_identity: 'CI',
        code_identity: 'V',
        identity: this.clientIdentity.trim()
      };

      const headers = new HttpHeaders({
        'content-type': 'application/json'
      });

      // Primera llamada: obtener el código de la persona
      const identityResponse: any = await lastValueFrom(
        this.http.post(`${environment.BASE_LA}/api/person/identity`, searchData, { headers })
      );
      
      if (identityResponse && identityResponse.resp && identityResponse.resp.person) {
        this.codePerson = identityResponse.resp.person.code;
        console.log('🆔 Código obtenido:', this.codePerson);
        
        // Segunda llamada: obtener datos completos por código
        const codeResponse: any = await lastValueFrom(
          this.http.post(`${environment.BASE_LA}/api/person/code`, { code: this.codePerson }, { headers })
        );
        
        if (codeResponse && codeResponse.resp) {
          this.clientData = codeResponse.resp;
          console.log('✅ Cliente encontrado:', this.clientData);
          console.log('🆔 Identitys:', this.clientData?.identitys);
          console.log('📞 Constacts:', this.clientData?.constacts);
          console.log('👤 Person:', this.clientData?.person);
          
          // Cargar transacciones automáticamente
          await this.loadTransactions();
        } else {
          alert('No se pudieron obtener los datos completos del cliente');
        }
      } else {
        alert('Cliente no encontrado');
      }
    } catch (error: any) {
      console.error('Error buscando cliente:', error);
      alert('Error buscando cliente: ' + (error.error?.message || error.message));
    } finally {
      this.loadingClient = false;
    }
  };

  /**
   * Limpiar búsqueda
   */
  clearSearch = () => {
    this.clientIdentity = '';
    this.clientData = null;
    this.transactions = [];
    this.codePerson = '';
    this.filters = {
      dateInitial: '',
      dateFinal: '',
      state: ''
    };
    this.currentPage = 1;
    this.stuckCurrentPage = 1;
    this.expandedTransaction = null;
    this.bitacoras = [];
    this.stuckTransactions = [];
  };

  /**
   * Cargar transacciones
   */
  loadTransactions = async () => {
    if (!this.codePerson) {
      return;
    }

    this.loadingTransactions = true;

    try {
      // Formatear fechas si existen
      let dateInitial = '';
      let dateFinal = '';

      if (this.filters.dateInitial) {
        const [year, month, day] = this.filters.dateInitial.split('-');
        dateInitial = `${month}/${day}/${year}`;
      }

      if (this.filters.dateFinal) {
        const [year, month, day] = this.filters.dateFinal.split('-');
        dateFinal = `${month}/${day}/${year}`;
      }

      const response = await this.bancoPlazaService.getTransactionsByPerson(
        this.codePerson,
        dateInitial,
        dateFinal,
        this.filters.state,
        this.currentPage,
        this.limit,
        this.sortOrder
      );

      if (response.success) {
        this.transactions = response.data.transactions;
        this.totalTransactions = response.data.pagination.total;
        this.totalPages = response.data.pagination.totalPages;
        console.log('✅ Transacciones cargadas:', this.transactions.length);
      }
    } catch (error: any) {
      console.error('Error cargando transacciones:', error);
      alert('Error cargando transacciones: ' + (error.error?.message || error.message));
    } finally {
      this.loadingTransactions = false;
    }
  };

  /**
   * Aplicar filtros
   */
  applyFilters = () => {
    this.currentPage = 1;
    this.loadTransactions();
  };

  /**
   * Limpiar filtros
   */
  clearFilters = () => {
    this.filters = {
      dateInitial: '',
      dateFinal: '',
      state: ''
    };
    this.currentPage = 1;
    this.loadTransactions();
  };

  /**
   * Cambiar página
   */
  changePage = (page: number) => {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadTransactions();
    }
  };

  toggleSortOrder = () => {
    this.sortOrder = this.sortOrder === -1 ? 1 : -1;
    this.currentPage = 1;
    this.loadTransactions();
  };

  /**
   * Toggle expandir/colapsar transacción
   */
  toggleTransaction = async (transactionNumber: number) => {
    if (this.expandedTransaction === transactionNumber) {
      // Colapsar
      this.expandedTransaction = null;
      this.bitacoras = [];
    } else {
      // Expandir y cargar bitácoras
      this.expandedTransaction = transactionNumber;
      await this.loadBitacoras(transactionNumber);
    }
  };

  /**
   * Cargar bitácoras de una transacción
   */
  loadBitacoras = async (transactionNumber: number) => {
    this.loadingBitacoras = true;
    this.bitacoras = [];

    try {
      const response = await this.bancoPlazaService.getBitacorasByTransaction(transactionNumber);
      
      if (response.success) {
        this.bitacoras = response.data;
        console.log('✅ Bitácoras cargadas:', this.bitacoras.length);
      }
    } catch (error: any) {
      console.error('Error cargando bitácoras:', error);
      alert('Error cargando bitácoras: ' + (error.error?.message || error.message));
    } finally {
      this.loadingBitacoras = false;
    }
  };

  /**
   * Cargar configuración
   */
  loadConfig = async () => {
    this.loadingConfig = true;

    try {
      const response = await this.bancoPlazaService.getConfig();
      
      if (response.success) {
        this.config = response.data;
        this.configForm = {
          max_token_attempts: response.data.max_token_attempts,
          max_payment_retries: response.data.max_payment_retries,
          max_verification_attempts: response.data.max_verification_attempts,
          max_cron_verification_attempts: response.data.max_cron_verification_attempts || 45,
          verification_delay_seconds: response.data.verification_delay_seconds
        };
        console.log('✅ Configuración cargada:', this.config);
      }
    } catch (error: any) {
      console.error('Error cargando configuración:', error);
      alert('Error cargando configuración: ' + (error.error?.message || error.message));
    } finally {
      this.loadingConfig = false;
    }
  };

  /**
   * Guardar configuración
   */
  saveConfig = async () => {
    if (this.configForm.max_token_attempts < 1 || this.configForm.max_token_attempts > 10) {
      alert('Los intentos de envío de token deben estar entre 1 y 10');
      return;
    }

    if (this.configForm.max_payment_retries < 1 || this.configForm.max_payment_retries > 10) {
      alert('Los intentos de ingreso de token deben estar entre 1 y 10');
      return;
    }

    if (this.configForm.max_verification_attempts < 1 || this.configForm.max_verification_attempts > 20) {
      alert('Los intentos de verificación deben estar entre 1 y 20');
      return;
    }

    if (this.configForm.verification_delay_seconds < 1 || this.configForm.verification_delay_seconds > 60) {
      alert('El tiempo entre verificaciones debe estar entre 1 y 60 segundos');
      return;
    }

    if (this.configForm.max_cron_verification_attempts < 10 || this.configForm.max_cron_verification_attempts > 200) {
      alert('Los intentos máximos del cron deben estar entre 10 y 200');
      return;
    }

    this.savingConfig = true;

    try {
      const response = await this.bancoPlazaService.updateConfig(this.configForm);
      
      if (response.success) {
        this.config = response.data;
        alert('Configuración actualizada exitosamente');
        console.log('✅ Configuración actualizada');
      }
    } catch (error: any) {
      console.error('Error guardando configuración:', error);
      alert('Error guardando configuración: ' + (error.error?.message || error.message));
    } finally {
      this.savingConfig = false;
    }
  };

  /**
   * Restaurar valores por defecto
   */
  restoreDefaults = () => {
    this.configForm = {
      max_token_attempts: 3,
      max_payment_retries: 3,
      max_verification_attempts: 10,
      verification_delay_seconds: 3,
      max_cron_verification_attempts: 45
    };
  };

  /**
   * Obtener clase CSS según estado
   */
  getStateClass = (state: string): string => {
    const stateClasses: any = {
      'PENDING': 'bg-gray-100 border-gray-300 text-gray-700',
      'TOKEN_GENERATED': 'bg-blue-100 border-blue-300 text-blue-700',
      'PROCESSING': 'bg-purple-100 border-purple-300 text-purple-700',
      'COMPLETE': 'bg-green-100 border-green-300 text-green-700',
      'FAILED': 'bg-red-100 border-red-300 text-red-700',
      'CANCELED': 'bg-red-200 border-red-400 text-red-800'
    };
    return stateClasses[state] || 'bg-gray-100 border-gray-300 text-gray-700';
  };

  /**
   * Obtener identidad del cliente
   * identitys es un array en clientData.identitys
   */
  getIdentity = (): string => {
    if (!this.clientData?.identitys || this.clientData.identitys.length === 0) return '';
    const identity = this.clientData.identitys[0];
    return `${identity?.type_identity?.name || ''} ${identity?.code_identity?.name || ''}-${identity?.identity || ''}`;
  };

  /**
   * Obtener teléfono del cliente
   * Los contactos están en clientData.constacts (array)
   */
  getContact = (): string => {
    if (!this.clientData?.constacts || this.clientData.constacts.length === 0) return '';
    const phoneCell = this.clientData.constacts.find((c: any) => c?.type_contact?.code === 'PHONE_CELL');
    if (phoneCell) return phoneCell.contact || '';
    return this.clientData.constacts[0]?.contact || '';
  };

  /**
   * Obtener email del cliente
   * Los contactos están en clientData.constacts (array)
   */
  getEmailContact = (): string => {
    if (!this.clientData?.constacts || this.clientData.constacts.length === 0) return '';
    const email = this.clientData.constacts.find((c: any) => c?.type_contact?.code === 'EMAIL');
    return email?.contact || '';
  };


  /**
   * Obtener nombre de acción según endpoint
   */
  getActionName = (endpoint: string): string => {
    const actionMapping: any = {
      '/start-payment': 'Iniciar Proceso (Token Solicitado)',
      '/resend-token': 'Reenviar Token',
      '/process-payment': 'Procesar Pago (Token Ingresado)',
      '/verify-payment': 'Verificar Pago',
      '/cancel-payment': 'Cancelación',
      '/cancel-transaction': 'Cancelar Transacción',
      '/verify-pending-payments': 'Verificar Pago (Masivo)'
    };
    return actionMapping[endpoint] || endpoint;
  };

  /**
   * Sanitizar URL de imagen
   */
  sanitize = (url: string) => {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  };

  /**
   * Formatear fecha para configuración
   */
  formatDate = (date: string): string => {
    if (!date) return '-';
    return new Date(date).toLocaleString('es-VE');
  };

  /**
   * Verificar pago (para transacciones PROCESSING)
   */
  verifyPayment = async (transaction: any): Promise<void> => {
    if (!confirm(`¿Desea verificar el pago de la transacción #${transaction.transaction_number}?`)) {
      return;
    }

    try {
      const response = await this.bancoPlazaService.verifyPayment(transaction.transaction_number);
      
      const paymentStatus =
        response?.verificationData?.verificationResponse?.paymentStatus ||
        response?.verificationResponse?.paymentStatus ||
        null;

      const statusMessage =
        response?.verificationData?.verificationResponse?.client_description ||
        response?.verificationData?.verificationResponse?.system_description ||
        response?.message ||
        null;

      if (!response?.success) {
        alert(`Error al verificar pago: ${response?.message || 'Error desconocido'}`);
      } else if (paymentStatus === 'APPROVED') {
        alert(statusMessage || 'Pago aprobado.');
      } else if (paymentStatus === 'REJECTED') {
        alert(statusMessage || 'Pago rechazado.');
      } else if (paymentStatus === 'PENDING') {
        // Caso típico: se encola / queda en PENDING_VERIFICATION
        const state = response?.verificationData?.state || response?.state || null;
        const stateText = state ? `Estado: ${state}. ` : '';
        alert(`${stateText}${statusMessage || 'Pago aún en proceso de verificación.'}`);
      } else {
        // Fallback: la llamada fue OK pero no hay paymentStatus
        alert(statusMessage || 'Verificación ejecutada.');
      }

      // Al cerrar el alert (es bloqueante), refrescar listas.
      if (this.codePerson) {
        await this.loadTransactions();
      }
      if (this.activeTab === 'stuck') {
        if (this.activeStuckSubTab === 'stuck') {
          await this.loadReallyStuckTransactions();
        } else if (this.activeStuckSubTab === 'unverified') {
          await this.loadStuckTransactions();
        } else if (this.activeStuckSubTab === 'anomalies') {
          await this.loadAnomalies();
        }
      }
      // Refrescar búsquedas activas
      if (this.payerIdentity && this.payerTransactions.length > 0) {
        await this.searchPayerTransactions(false);
      }
      if (this.searchTransactionNumber && this.searchTransactions.length > 0) {
        await this.searchTransactionsByNumber(false);
      }
    } catch (error: any) {
      console.error('Error verificando pago:', error);
      alert(`Error al verificar pago: ${error.error?.message || error.message || 'Error desconocido'}`);
    }
  };

  /**
   * Liberar transacción de la cola de verificación y marcar para verificación manual
   */
  releaseFromQueue = async (transactionNumber: number, createPayment: boolean): Promise<void> => {
    const confirmMessage = createPayment 
      ? `¿Marcar transacción ${transactionNumber} para verificación manual?\n\nEsta acción marcará la transacción como MANUAL_COMPLETE. El pago ya está creado y por conciliar.\n\nDeberás verificar con Banco Plaza si el débito fue procesado y luego conciliar o anular el pago según corresponda.`
      : `¿Está seguro de liberar la transacción ${transactionNumber} de la cola?`;
    
    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await this.bancoPlazaService.releaseVerificationQueue(transactionNumber, createPayment);
      
      if (response.success) {
        if (createPayment) {
          const paymentInfo = response.data.paymentCode ? `\nCódigo de pago: ${response.data.paymentCode}` : '';
          alert(`✅ Transacción marcada para verificación manual exitosamente.${paymentInfo}\n\n⚠️ Recuerda verificar con Banco Plaza si el débito fue procesado antes de conciliar o anular el pago.`);
        } else {
          alert('Transacción liberada de la cola exitosamente.');
        }
        
        // Refrescar la lista de atascadas
        await this.loadReallyStuckTransactions();
        
        // Si hay cliente buscado, refrescar su historial
        if (this.codePerson) {
          await this.loadTransactions();
        }
        
        // Refrescar búsquedas activas
        if (this.payerIdentity && this.payerTransactions.length > 0) {
          await this.searchPayerTransactions(false);
        }
        if (this.searchTransactionNumber && this.searchTransactions.length > 0) {
          await this.searchTransactionsByNumber(false);
        }
      } else {
        alert(`Error: ${response.message || 'No se pudo liberar la transacción'}`);
      }
    } catch (error: any) {
      console.error('Error liberando transacción:', error);
      alert(`Error al liberar transacción: ${error.error?.message || error.message || 'Error desconocido'}`);
    }
  };

  /**
   * Cancelar transacción (marca como CANCELED)
   */
  cancelTransaction = async (transaction: any): Promise<void> => {
    if (!confirm(`¿Está seguro de cancelar la transacción #${transaction.transaction_number}? Esta acción marcará la transacción como CANCELADA.`)) {
      return;
    }

    try {
      const response = await this.bancoPlazaService.cancelTransaction(transaction.transaction_number);
      
      if (response?.success) {
        alert(`Transacción #${transaction.transaction_number} cancelada exitosamente.`);
      } else {
        alert(`Error al cancelar transacción: ${response?.message || 'Error desconocido'}`);
      }

      // Refrescar listas
      if (this.codePerson) {
        await this.loadTransactions();
      }
      if (this.activeTab === 'stuck') {
        if (this.activeStuckSubTab === 'stuck') {
          await this.loadReallyStuckTransactions();
        } else if (this.activeStuckSubTab === 'unverified') {
          await this.loadStuckTransactions();
        } else if (this.activeStuckSubTab === 'anomalies') {
          await this.loadAnomalies();
        }
      }
      if (this.payerIdentity && this.payerTransactions.length > 0) {
        await this.searchPayerTransactions(false);
      }
      if (this.searchTransactionNumber && this.searchTransactions.length > 0) {
        await this.searchTransactionsByNumber(false);
      }
    } catch (error: any) {
      console.error('Error cancelando transacción:', error);
      alert(`Error al cancelar transacción: ${error.error?.message || error.message || 'Error desconocido'}`);
    }
  };

  /**
   * Completar manualmente transacción con timeout
   */
  completeTimeoutTransaction = async (transactionNumber: number): Promise<void> => {
    if (!confirm(`¿Está seguro de completar manualmente la transacción #${transactionNumber}?\n\nEsto creará un pago por conciliar y marcará la transacción como MANUAL_COMPLETE.`)) {
      return;
    }

    try {
      const response = await this.bancoPlazaService.completeTimeoutTransaction(transactionNumber);
      
      if (response?.success) {
        alert(`Transacción #${transactionNumber} completada manualmente.\n\nSe creó el pago: ${response.data?.paymentCode || 'N/A'}`);
      } else {
        alert(`Error al completar transacción: ${response?.message || 'Error desconocido'}`);
      }

      // Refrescar listas
      if (this.codePerson) {
        await this.loadTransactions();
      }
      if (this.activeTab === 'stuck') {
        if (this.activeStuckSubTab === 'stuck') {
          await this.loadReallyStuckTransactions();
        } else if (this.activeStuckSubTab === 'unverified') {
          await this.loadStuckTransactions();
        } else if (this.activeStuckSubTab === 'anomalies') {
          await this.loadAnomalies();
        }
      }
      if (this.payerIdentity && this.payerTransactions.length > 0) {
        await this.searchPayerTransactions(false);
      }
      if (this.searchTransactionNumber && this.searchTransactions.length > 0) {
        await this.searchTransactionsByNumber(false);
      }
    } catch (error: any) {
      console.error('Error completando transacción con timeout:', error);
      alert(`Error al completar transacción: ${error.error?.message || error.message || 'Error desconocido'}`);
    }
  };

  /**
   * Abrir modal de detalle de pago
   */
  openPaymentDetailModal = async (transaction: any): Promise<void> => {
    if (!transaction.codePayment) {
      alert('Esta transacción no tiene código de pago');
      return;
    }

    // Intentar obtener codePerson de la transacción o del contexto
    const codePerson = transaction.person_code || transaction.params?.person_code || this.codePerson;
    if (!codePerson) {
      alert('No se encontró la información del cliente');
      return;
    }

    this.isLoadingPaymentDetail = true;
    this.isPaymentDetailModalOpen = true;
    this.selectedPaymentDetail = null;

    try {
      console.log('🔍 Consultando pago con código:', transaction.codePayment, 'para persona:', codePerson);
      const response = await this.userService.getPaymentByCode(transaction.codePayment, codePerson);
      console.log('📡 Respuesta completa del API:', response);
      
      let paymentData: any;
      if (Array.isArray(response)) {
        paymentData = response;
      } else {
        paymentData = response?.resp || response?.res;
      }
      console.log('📊 Payment data extraído:', paymentData);
      
      if (paymentData?.length > 0) {
        this.selectedPaymentDetail = paymentData[0];
        this.isLoadingPaymentDetail = false;
        console.log('✅ Detalle del pago obtenido:', this.selectedPaymentDetail);
      } else {
        console.log('❌ paymentData está vacío o no tiene elementos');
        alert('No se encontró información del pago');
        this.closePaymentDetailModal();
      }
    } catch (error) {
      console.error('❌ Error al obtener detalle del pago:', error);
      alert('Error al consultar el detalle del pago');
      this.closePaymentDetailModal();
    } finally {
      this.isLoadingPaymentDetail = false;
    }
  };

  /**
   * Cerrar modal de detalle de pago
   */
  closePaymentDetailModal = (): void => {
    this.isPaymentDetailModalOpen = false;
    this.selectedPaymentDetail = null;
    this.isLoadingPaymentDetail = false;
  };

  /**
   * Obtener nombre de banco
   */
  getBankName = (bankCode: string): string => {
    const banks: any = {
      '0102': 'Banco de Venezuela',
      '0104': 'Banco Venezolano de Crédito',
      '0105': 'Banco Mercantil',
      '0108': 'Banco Provincial',
      '0114': 'Bancaribe',
      '0115': 'Banco Exterior',
      '0128': 'Banco Caroní',
      '0134': 'Banesco',
      '0137': 'Banco Sofitasa',
      '0138': 'Banco Plaza',
      '0146': 'Bangente',
      '0151': 'BFC Banco Fondo Común',
      '0156': '100% Banco',
      '0157': 'DelSur Banco Universal',
      '0163': 'Banco del Tesoro',
      '0166': 'Banco Agrícola de Venezuela',
      '0168': 'Bancrecer',
      '0169': 'Mi Banco',
      '0171': 'Banco Activo',
      '0172': 'Bancamiga',
      '0173': 'Banco Internacional de Desarrollo',
      '0174': 'Banplus',
      '0175': 'Banco Bicentenario',
      '0177': 'Banco de la Fuerza Armada Nacional Bolivariana',
      '0191': 'Banco Nacional de Crédito'
    };
    return banks[bankCode] || bankCode;
  };

  /**
   * Redirigir a imagen
   */
  redirectIMG = (url: string): void => {
    window.open(url, '_blank');
  };

  /**
   * Sincronizar estados de transacciones MANUAL_COMPLETE
   * Actualiza transacciones según el estado de sus pagos:
   * - Pago aprobado → COMPLETE
   * - Pago anulado → CANCELED
   * - Pago por conciliar → permanece MANUAL_COMPLETE
   */
  syncManualCompleteStates = async (): Promise<void> => {
    if (this.syncingManualComplete) {
      return;
    }

    this.syncingManualComplete = true;

    try {
      console.log('🔄 [BP Console] Iniciando sincronización de estados MANUAL_COMPLETE...');
      const response = await this.bancoPlazaService.syncManualCompleteTransactions();

      if (response?.success) {
        const data = response.data;
        
        // Mostrar resumen en alert bonito
        let message = `✅ Sincronización completada\n\n`;
        message += `📊 Total procesadas: ${data.total}\n`;
        message += `✅ Aprobadas → COMPLETE: ${data.complete}\n`;
        message += `❌ Anuladas → CANCELED: ${data.canceled}\n`;
        message += `⏳ Pendientes conciliación: ${data.pending}\n`;
        
        if (data.noPayment > 0) {
          message += `⚠️ Sin pago encontrado: ${data.noPayment}\n`;
        }

        // Log detallado para verificación
        if (data.details && data.details.length > 0) {
          console.log('📋 Detalles de sincronización:');
          data.details.forEach((detail: { transaction_number: number; newState: string; reason: string }) => {
            console.log(`  TX ${detail.transaction_number}: ${detail.newState} (${detail.reason})`);
          });
        }

        alert(message);

        // Refrescar la lista de completadas manuales
        await this.loadStuckTransactions();
      } else {
        alert(`Error en sincronización: ${response?.message || 'Error desconocido'}`);
      }
    } catch (error: any) {
      console.error('❌ Error sincronizando estados:', error);
      alert(`Error al sincronizar estados: ${error.error?.message || error.message || 'Error desconocido'}`);
    } finally {
      this.syncingManualComplete = false;
    }
  };
}
